import { ISelectOption } from '@private/components';
import { NOTIF_TYPES } from '@private/notifications';
import Big from 'big.js';
import { endOfMonth } from 'date-fns/endOfMonth';
import { format as formatDate } from 'date-fns/format';
import { startOfMonth } from 'date-fns/startOfMonth';
import { toZonedTime } from 'date-fns-tz';
import { i18n as I18n, TFunction } from 'i18next';

import {
  BASE_TIME_MASK,
  DAY_MSEC,
  HOUR_MSEC,
  KB_BYTES_SIZE,
  MINUTE_MSEC,
  STATUSES,
  TIMEZONE_MASK_NEW,
  TURKEY,
  WEEK_MSEC,
} from 'constants/common';
import { notificationService } from 'utils/notificationService';

import { IGist, INullable, ISortingCallback, QueryError, TSearch } from './types';

export const sleep = (ms: number): Promise<void> => {
  // eslint-disable-next-line no-promise-executor-return
  return new Promise((resolve) => setTimeout(resolve, ms));
};

export const copyToClipBoard = async (text: string) => {
  try {
    await navigator.clipboard.writeText(text);
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error(err);
  }
};

export const formatByteToKB = (bytes: number, toFixed = 1): string => {
  return `${(bytes / KB_BYTES_SIZE).toFixed(toFixed)} Kb`;
};

export const getUtcDate = (value: string) => {
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return toZonedTime(`${value}T00:00:00Z`, 'UTC');
  }
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?$/.test(value)) {
    // Append 'Z' to make it UTC if there is no such
    return toZonedTime(`${value}Z`, 'UTC');
  }
  return toZonedTime(value, 'UTC');
};

export const getFormattedDate = (
  dateString?: INullable<string>,
  ignoreList?: string[],
): string => {
  if (!dateString) {
    return '';
  }

  const date = getUtcDate(dateString);

  const formatter = new Intl.DateTimeFormat('ru', {
    day: ignoreList?.includes('day') ? undefined : '2-digit',
    hour: ignoreList?.includes('hour') ? undefined : 'numeric',
    minute: ignoreList?.includes('minute') ? undefined : 'numeric',
    month: ignoreList?.includes('month') ? undefined : '2-digit',
    second: ignoreList?.includes('second') ? undefined : 'numeric',
    year: ignoreList?.includes('year') ? undefined : 'numeric',
  });

  try {
    return formatter.format(date);
  } catch {
    return dateString;
  }
};

export const getFormattedShortDate = (dateString?: INullable<string>): string => {
  if (!dateString) {
    return '';
  }

  try {
    return formatDate(getUtcDate(dateString), BASE_TIME_MASK);
  } catch {
    return dateString;
  }
};

export const getFormattedCountry = (countryCode?: string, locale = 'en'): string => {
  if (!countryCode) {
    return '';
  }

  if (countryCode === 'TR' && locale === 'en') {
    return TURKEY;
  }

  // `Intl.DisplayNames.of` throws a RangeError on structurally-invalid region codes
  // (empty string, 3-letter or otherwise malformed values). Legacy records can hold
  // such codes, so fall back to the raw code — one bad value must not crash a render.
  try {
    const formatter = new Intl.DisplayNames([locale], { type: 'region' });

    return formatter.of(countryCode) || countryCode;
  } catch {
    return countryCode;
  }
};

export const getNormalName = (text: string): string => {
  if (!text) {
    return '';
  }

  const normalizedArr = text
    .split('_')
    .map((item) => item[0].toUpperCase() + item.slice(1).toLowerCase());
  return normalizedArr.join(' ');
};

export const normalizeCamelCaseName = (text: string) =>
  text
    .split(/(?=[A-Z])/)
    .map((item) => item.charAt(0).toUpperCase() + item.slice(1))
    .join(' ');

export const getHMfromMilliseconds = (milliseconds: INullable<number | undefined>) => {
  if (!milliseconds) {
    return null;
  }

  const seconds = Math.floor(milliseconds / 1000);
  let minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);

  minutes %= 60;

  return `${hours}h ${minutes.toString().padStart(2, '0')}min`;
};

interface ISetNestedProp<T> {
  key: string;
  obj: T;
  value: unknown;
}

export const setNestedProp = <T>({
  key,
  obj,
  value,
}: ISetNestedProp<T>): { oldValue: unknown; updatedObj: T } => {
  if (!key.includes('.')) {
    const updatedObj = { ...obj, [key]: value };
    return { oldValue: obj[key as keyof typeof obj], updatedObj };
  }
  const currentObj = structuredClone(obj);
  let oldValue: unknown = null;
  let currentValue = currentObj;
  const keys = key.split('.').reverse() as (keyof T)[];

  while (keys.length) {
    const key = keys.pop();
    if (key && keys.length) {
      currentValue = currentValue[key] as unknown as T;
    }

    if (key && !keys.length) {
      oldValue = currentValue[key as keyof T];
      currentValue[key as keyof T] = value as T[keyof T];
    }
  }

  return { oldValue, updatedObj: currentObj };
};

export const getSortingCallback = <T>({
  callback,
  columns,
  filters,
  index,
  isDefaultGridSorting,
  sort,
}: ISortingCallback<T>) => {
  return {
    onSortingChange:
      filters?.[columns[index].dataKey]?.sortable || isDefaultGridSorting
        ? callback
        : undefined,
    sortedColumn: sort?.orders?.find(
      (column) => column.property === columns[index]?.dataKey,
    ),
  };
};

export const mathRoundNumber = (number?: INullable<number>, digits = 2) => {
  if (number === undefined || number === null) {
    return '';
  }
  const countOfDigits = digits < 1 ? 0 : digits;
  if (countOfDigits === 0) {
    return Math.round(number);
  }
  const roundNumber = 10 ** countOfDigits;
  return Math.round(number * roundNumber) / roundNumber;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const readJWTData = (jwt?: string): any => {
  if (!jwt) {
    return;
  }
  const encodedDataParts = jwt.split('.');
  if (encodedDataParts.length !== 3) {
    return;
  }
  const encodedData = encodedDataParts[1];
  const decoded = window.atob(encodedData);
  return JSON.parse(decoded);
};

export const getPreviousMonthDate = (): Date => {
  const date = new Date();

  const previousMonth = new Date(date.getTime());
  previousMonth.setDate(0);

  return previousMonth;
};

/* eslint-disable @typescript-eslint/no-explicit-any */
interface IGetFormErrors {
  errors?: Record<string, any>;
  getTranslation: TFunction;
}

export const joinErrorMessages = (messages: unknown[]): string =>
  [...new Set(messages)].join('; ');

export const getFormErrors = ({ errors, getTranslation: t }: IGetFormErrors) => {
  const newObj: Record<string, any> = {};
  /* eslint-enable @typescript-eslint/no-explicit-any */
  if (!errors) {
    return newObj;
  }

  const getTranslation = (errorMsg: string) => {
    const [intlKey, from, to, unit] = errorMsg.split(':');
    if (errorMsg.includes('mustBeIntegerInRange')) {
      return t(intlKey, { from: from || '1', to: to || '100', unit } as never);
    }
    return t(intlKey, { from, to, unit } as never);
  };

  /* eslint-disable @typescript-eslint/no-explicit-any */
  const setNestedErrors = (
    obj: Record<string, any>,
    keys: string[],
    value: string | string[],
  ) => {
    let nestedObj = obj;
    for (let i = 0; i < keys.length - 1; i += 1) {
      const currentKey = keys[i];
      if (!(currentKey in nestedObj)) {
        nestedObj[currentKey] = Number.isNaN(Number(keys[i + 1])) ? {} : [];
      }
      nestedObj = nestedObj[currentKey];
    }

    const errorsList = Array.isArray(value) ? value : [value];
    nestedObj[keys[keys.length - 1]] = joinErrorMessages(errorsList.map(getTranslation));
  };

  Object.entries(errors).forEach(([key, value]) => {
    const nestedKeys = key.split('[');
    if (nestedKeys.length === 1) {
      const path = key.split('.');
      let currentObj = newObj;

      path.forEach((pathKey, index) => {
        if (index === path.length - 1) {
          const errorsValue = errors[key];
          const errorsList = Array.isArray(errorsValue) ? errorsValue : [errorsValue];
          currentObj[pathKey] = joinErrorMessages(errorsList.map(getTranslation));
        } else {
          currentObj[pathKey] = currentObj[pathKey] || {};
          currentObj = currentObj[pathKey];
        }
      });
    } else {
      const splittedKey = key.split(/[\\[\].]+/).filter(Boolean);
      setNestedErrors(newObj, splittedKey, value);
    }
  });

  return newObj;
};

/* eslint-disable @typescript-eslint/no-explicit-any */
export const getFlatFormErrors = ({ errors, getTranslation }: IGetFormErrors) => {
  const newObj: Record<string, any> = {};

  if (!errors) {
    return newObj;
  }

  Object.keys(errors).forEach((key) => {
    const errorsValue = errors[key];
    const errorsList = Array.isArray(errorsValue) ? errorsValue : [errorsValue];

    newObj[key] = joinErrorMessages(
      errorsList.map((errorMsg) => {
        const [intlKey, from, to, unit] = String(errorMsg ?? '').split(':');
        return getTranslation(intlKey as never, { from, to, unit } as never);
      }),
    );
  });

  return newObj;
};

interface ITranslateServerMessage {
  getTranslation: TFunction;
  i18n: I18n;
  message?: string;
}

/**
 * Resolves a top-level server message that may itself be an i18n key (optionally
 * with `:`-delimited params, matching `getFlatFormErrors`). Falls back to the raw
 * string when the key is unknown, so a filter-level error is never surfaced as an
 * untranslated key.
 */
export const translateServerMessage = ({
  getTranslation,
  i18n,
  message,
}: ITranslateServerMessage) => {
  const [intlKey, from, to, unit] = message?.split(':') ?? [];

  if (!message || !i18n.exists(intlKey)) {
    return message;
  }

  return getTranslation(intlKey as never, { from, to, unit });
};

interface IGetFieldBoundErrors {
  error: QueryError;
  getTranslation: TFunction;
  i18n: I18n;
}

export const getFieldBoundErrorMessages = ({
  error,
  getTranslation,
  i18n,
}: IGetFieldBoundErrors): Record<string, string> => {
  const messages: Record<string, string> = {};

  Object.entries(error?.response?.data?.errors ?? {}).forEach(([field, codes]) => {
    // Codes arrive as `Record<string, string[]>` on the wire even though
    // `IRespError` types them as strings, so flatten before translating.
    const translated = joinErrorMessages(
      [codes]
        .flat()
        .filter((code) => i18n.exists(code.split(':')[0]))
        .map((code) => translateServerMessage({ getTranslation, i18n, message: code })),
    );

    if (translated) {
      messages[field] = translated;
    }
  });

  return messages;
};

interface IGetFieldBoundErrorMessage extends IGetFieldBoundErrors {
  fallbackMessage: string;
}

export const getFieldBoundErrorMessage = ({
  error,
  fallbackMessage,
  getTranslation,
  i18n,
}: IGetFieldBoundErrorMessage): string => {
  const fieldErrors = joinErrorMessages(
    Object.values(getFieldBoundErrorMessages({ error, getTranslation, i18n })),
  );

  if (fieldErrors) {
    return fieldErrors;
  }

  return (
    translateServerMessage({
      getTranslation,
      i18n,
      message: error?.response?.data?.message,
    }) || fallbackMessage
  );
};

/**
 * Translates a backend validation message that carries the i18n key inline,
 * either as a bare key or in the `"<field>: <key>[:<from>[:<to>[:<unit>]]]"`
 * shape some endpoints use instead of the structured `errors` map. Returns the
 * raw message unchanged when it doesn't look like an i18n key (no dotted key).
 */
const translateBackendMessage = (message: string, getTranslation: TFunction): string => {
  const separator = ': ';
  const keyPart = message.includes(separator)
    ? message.slice(message.indexOf(separator) + separator.length)
    : message;
  const [intlKey, from, to, unit] = keyPart.split(':');

  if (!intlKey.includes('.')) {
    return message;
  }

  return getTranslation(intlKey as never, { from, to, unit });
};

/**
 * Builds the notification message for an inline-edit save failure. Inline edit
 * can't highlight the invalid field the way the group-edit form does, so the
 * specific backend validation reason(s) are surfaced in the toast instead. The
 * reason may arrive either in the structured `errors` map or embedded in
 * `message`; both are translated, falling back to the caller's generic message.
 */
export const getInlineEditErrorMessage = (
  error: QueryError,
  getTranslation: TFunction,
  fallbackMessage: string,
): string => {
  const data = error?.response?.data;

  const fieldErrors = getFlatFormErrors({ errors: data?.errors, getTranslation });
  const joinedFieldErrors = joinErrorMessages(Object.values(fieldErrors));

  if (joinedFieldErrors) {
    return joinedFieldErrors;
  }

  if (data?.message) {
    return translateBackendMessage(data.message, getTranslation);
  }

  return fallbackMessage;
};

/**
 * Normalizes a cleared inline-edit field: an empty string becomes `null` so the
 * PATCH payload sends `null` instead of `""` (e.g. `minCommission: null`).
 */
export const normalizeInlineEditValue = <T>(value: T): null | T =>
  value === '' ? null : value;

/* eslint-disable @typescript-eslint/no-explicit-any */
export const getFormTouched = (
  originalObj?: Record<string, any>,
): Record<string, any> => {
  const newObj: Record<string, any> = {};
  /* eslint-enable @typescript-eslint/no-explicit-any */

  if (!originalObj) return newObj;

  Object.keys(originalObj).forEach((key) => {
    // "filters[0].operation" -> "filters.0.operation"
    const path = key.replace(/\[(\d+)\]/g, '.$1').split('.');
    let currentObj = newObj;

    path.forEach((pathKey, index) => {
      const isLast = index === path.length - 1;

      if (isLast) {
        currentObj[pathKey] = true;
        return;
      }

      if (!currentObj[pathKey]) {
        const next = path[index + 1];
        currentObj[pathKey] = /^\d+$/.test(next) ? [] : {};
      }

      currentObj = currentObj[pathKey];
    });
  });

  return newObj;
};

export const formErrorsExist = (
  errors: Record<string, unknown>,
  omitKeys: string[] = [],
): boolean => {
  const entries = Object.entries(errors);
  for (const [key, value] of entries) {
    if (typeof value !== 'object' && value && !omitKeys.includes(key)) {
      return true;
    }
    if (typeof value === 'object') {
      return formErrorsExist(value as Record<string, unknown>);
    }
  }

  return false;
};

export const checkNumberValue = (
  value?: number,
  returnText?: number | string,
): number | string => (value === undefined ? '-' : returnText || value);

export const getSelectList = <T extends IGist>(
  data?: null | T[],
): ISelectOption[] | undefined => {
  return data?.map((item) => ({
    label: item.name,
    value: item.id,
  }));
};

export const alphabetSort = (a: ISelectOption, b: ISelectOption) => {
  if (a.label! < b.label!) {
    return -1;
  }
  if (a.label! > b.label!) {
    return 1;
  }
  return 0;
};

// Country name with its ISO 3166-1 alpha-2 code as a suffix, e.g. `Ukraine (UA)`.
// Used for country *selection* controls so the code is visible and searchable;
// the bare code stays the option `value`, so saved payloads are unchanged.
export const getCountryLabelWithCode = (countryCode: string): string =>
  `${getFormattedCountry(countryCode)} (${countryCode})`;

export const getCountryOptions = (data: string[] = []): ISelectOption[] => {
  return data
    .map((countryCode) => ({
      label: getCountryLabelWithCode(countryCode),
      value: countryCode,
    }))
    .sort(alphabetSort);
};

const CONFLICT_NOTIFICATION_DISMISS_MSEC = 10000;

interface IShowMutationErrorNotification {
  error: QueryError;
  fallbackMessage: string;
  getTranslation: TFunction;
  updateId: string;
}

/**
 * Replaces a mutation's loading toast (matched by `updateId`) with its error toast.
 * A conflict response carries the reason as an i18n key in `message`, so that
 * localized reason is surfaced instead of the caller's generic fallback.
 *
 * Must be called from the mutation's `onError`: `getQueryBoundary` via `throwOnError`
 * is not a substitute, because React Query only evaluates `throwOnError` while the
 * component owning the mutation renders. A mutation owned by a grid cell loses its
 * observer before the response lands (`@private/data-grid` remounts every cell on each
 * `DataGrid` re-render), which leaves the loading toast spinning forever.
 */
export const showMutationErrorNotification = ({
  error,
  fallbackMessage,
  getTranslation,
  updateId,
}: IShowMutationErrorNotification) => {
  const isConflictError = error?.response?.status === STATUSES.CONFLICT;
  const serverMessage = error?.response?.data?.message;

  notificationService.show({
    dismissIn: isConflictError ? CONFLICT_NOTIFICATION_DISMISS_MSEC : undefined,
    message:
      isConflictError && serverMessage
        ? translateBackendMessage(serverMessage, getTranslation)
        : fallbackMessage,
    title: getTranslation('notifications.errorTitle'),
    type: NOTIF_TYPES.ERROR,
    updateId,
  });
};

export const getQueryBoundary = (error: QueryError, t: TFunction, updateId?: string) => {
  const isConflictError = error?.response?.status === STATUSES.CONFLICT;

  if (isConflictError) {
    notificationService.show({
      dismissIn: CONFLICT_NOTIFICATION_DISMISS_MSEC,
      message: t(error?.response?.data?.message as never),
      title: t('notifications.errorTitle'),
      type: NOTIF_TYPES.ERROR,
      updateId,
    });

    return false;
  }

  const errorsList =
    error.response?.data?.errors || error.response?.data?.fieldMessages || {};

  return !Object.keys(errorsList).length;
};

interface IGetGridPath {
  path: string;
  search: TSearch;
}

export const getGridPathWithFilters = ({ path, search }: IGetGridPath) => {
  return `${path}?${new URLSearchParams({
    pageFilters: JSON.stringify({ search }),
  }).toString()}`;
};

const TEMPLATE_TOKEN_PATTERN = /\{\{[^{}]*\}\}|\{\$[^{}$]*\$\}/g;
const TEMPLATE_SENTINEL_PREFIX = '__MP_TPL_VAR_';

interface ITemplateToken {
  hasInjectedQuotes: boolean;
  original: string;
  sentinel: string;
}

const matchTemplateTokenAt = (input: string, position: number) => {
  const remainder = input.slice(position);
  const localPattern = new RegExp(TEMPLATE_TOKEN_PATTERN.source);
  const match = remainder.match(localPattern);
  if (!match || match.index !== 0) return null;
  return match[0];
};

const replaceTemplateTokensWithSentinels = (input: string) => {
  const tokens: ITemplateToken[] = [];
  let sanitized = '';
  let inString = false;
  let position = 0;

  while (position < input.length) {
    const char = input[position];

    if (inString && char === '\\' && position + 1 < input.length) {
      sanitized += char + input[position + 1];
      position += 2;
      continue;
    }

    if (char === '"') {
      inString = !inString;
      sanitized += char;
      position += 1;
      continue;
    }

    const token = matchTemplateTokenAt(input, position);

    if (token) {
      const sentinel = `${TEMPLATE_SENTINEL_PREFIX}${tokens.length}__`;
      const hasInjectedQuotes = !inString;
      tokens.push({ hasInjectedQuotes, original: token, sentinel });
      sanitized += hasInjectedQuotes ? `"${sentinel}"` : sentinel;
      position += token.length;
      continue;
    }

    sanitized += char;
    position += 1;
  }

  return { sanitized, tokens };
};

const restoreTemplateTokens = (formatted: string, tokens: ITemplateToken[]) => {
  let restored = formatted;
  tokens.forEach(({ hasInjectedQuotes, original, sentinel }) => {
    const placeholder = hasInjectedQuotes ? `"${sentinel}"` : sentinel;
    restored = restored.replace(placeholder, () => original);
  });
  return restored;
};

export const formatJSONWithVars = (input: string): null | string => {
  try {
    const { sanitized, tokens } = replaceTemplateTokensWithSentinels(input);
    const parsed = JSON.parse(sanitized);
    const formatted = JSON.stringify(parsed, null, 2);
    return restoreTemplateTokens(formatted, tokens);
  } catch {
    return null;
  }
};

export const isValidJsonBody = (value: string): boolean => {
  if (!value) return true;

  const trimmed = value.trim();
  if (!trimmed || trimmed.startsWith('<')) return true;

  try {
    const { sanitized } = replaceTemplateTokensWithSentinels(value);
    JSON.parse(sanitized);
    return true;
  } catch {
    return false;
  }
};

export const formatXML = (xml: string) => {
  const tab = '  ';
  const nl = '\n';

  let formatted = '';
  let indent = '';

  const nodes = xml.trim().slice(1, -1).split(/>\s*</);

  if (nodes[0][0] === '?') {
    formatted += `<${nodes.shift()}>${nl}`;
  }

  for (let i = 0; i < nodes.length; i += 1) {
    const node = nodes[i];

    if (node[0] === '/') {
      indent = indent.slice(tab.length); // decrease indent
    }

    formatted += `${indent}<${node}>${nl}`;

    if (node[0] !== '/' && node[node.length - 1] !== '/' && node.indexOf('</') === -1) {
      indent += tab; // increase indent
    }
  }
  return formatted;
};

export const getGridActionsWidth = (actionsCount: number) =>
  actionsCount === 2 ? 115 : 75;

export const convertMilliseconds = (milliseconds?: null | number) => {
  if (milliseconds) {
    const weeks = Math.floor(milliseconds / WEEK_MSEC);
    const days = Math.floor((milliseconds % WEEK_MSEC) / DAY_MSEC);
    const hours = Math.floor((milliseconds % DAY_MSEC) / HOUR_MSEC);
    const minutes = Math.floor((milliseconds % HOUR_MSEC) / MINUTE_MSEC);

    return { days, hours, minutes, weeks };
  }

  return { days: null, hours: null, minutes: null, weeks: null };
};

export const getDHMfromMilliseconds = (milliseconds: number | undefined) => {
  if (!milliseconds) {
    return null;
  }

  const { days, hours, minutes, weeks } = convertMilliseconds(milliseconds);

  return `${Number(weeks) * 7 + Number(days)}d ${hours}h ${minutes}min`;
};

export const getWDHMfromMilliseconds = (milliseconds: number | undefined) => {
  if (!milliseconds) {
    return null;
  }

  const { days, hours, minutes, weeks } = convertMilliseconds(milliseconds);

  return `${weeks}w ${days}d ${hours}h ${minutes}min`;
};

export const isDefinedValue = (value: unknown): boolean => {
  return value !== undefined && value !== null && value !== '';
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const debounce = <F extends (...args: any[]) => void>(func: F, delay: number) => {
  let timerId: NodeJS.Timeout;

  return (...args: Parameters<F>): void => {
    if (timerId) {
      clearTimeout(timerId);
    }

    timerId = setTimeout(() => {
      func(...args);
    }, delay);
  };
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const throttle = <T extends (...args: any[]) => void>(
  func: T,
  delay: number,
): ((...args: Parameters<T>) => void) => {
  let lastCall = 0;
  let timeout: null | ReturnType<typeof setTimeout> = null;
  let lastArgs: null | Parameters<T> = null;

  return (...args: Parameters<T>) => {
    const now = Date.now();

    if (now - lastCall >= delay) {
      lastCall = now;
      func(...args);
    } else {
      lastArgs = args;
      if (!timeout) {
        timeout = setTimeout(
          () => {
            if (lastArgs) {
              func(...lastArgs);
              lastCall = Date.now();
            }
            timeout = null;
            lastArgs = null;
          },
          delay - (now - lastCall),
        );
      }
    }
  };
};

export const getCurrentMonthPeriod = () => {
  const currDate = new Date();
  const startDate = formatDate(startOfMonth(currDate), TIMEZONE_MASK_NEW);
  const endDate = formatDate(endOfMonth(currDate), TIMEZONE_MASK_NEW);

  return { endDate, startDate };
};

export const binRangeValidator = (value: string) => {
  const isValidRegExp = /^[0-9:]*$/.test(value);
  const splittedList: string[] = value.split(':');
  const everyIsValid = splittedList.every((item) => item.length <= 11);

  return isValidRegExp && everyIsValid;
};

export const phonePrefixValidator = (value: string) => {
  const isValidRegExp = /^[0-9:]*$/.test(value);
  const splittedList: string[] = value.split(':');
  const everyIsValid = splittedList.every((item) => item.length <= 9);

  return isValidRegExp && everyIsValid && value.length <= 1024;
};

export const getNumberString = (amount?: number | string, decimalPlaces?: number) => {
  if (amount === undefined) return '';
  return (typeof amount === 'number' || amount.includes('e')) &&
    Number.isFinite(Number(amount))
    ? new Big(amount).toFixed(decimalPlaces)
    : amount;
};

export const getIdFromCode = (code?: string): string => {
  return code?.replace(/\D/g, '') || '';
};
